'use client'

import { useTranslations } from 'next-intl'
import { ChangeEvent, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import cartApiRequest from '@/api-requests/cart'
import { useSession } from '@/context/SessionProvider'
import { cn, getPriceByQuantity as getPrice, handleApiError, valueByMultiples } from '@/lib/utils'
import { useAppDispatch } from '@/redux/hooks'
import { addItem } from '@/redux/slices/orderSlice'
import { CartItem } from '@/types/cart.type'
import { CategoryProductDetail } from '@/types/home.type'
import { Product, ProductFullSearchData, ProductPrice } from '@/types/product.type'

import Button from '../ui/Button'
import Icon from '../ui/icon'
import clsx from 'clsx'

type ButtonCartGroupProps = {
  productId: number
  prices: ProductPrice[]
  className?: string
  multiples: number
  minQuantity: number
  product: Product | ProductFullSearchData | CategoryProductDetail
  buttonSize?: 'default' | 'xs' | 'sm' | 'md' | 'lg' | 'icon'
  isDisabled?: boolean
}

export default function ButtonCartGroup({ product, prices, className, multiples, productId, buttonSize = 'sm', minQuantity, isDisabled = false }: ButtonCartGroupProps) {
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState<string>(minQuantity?.toString())
  const [categoryExtraId, setCategoryExtraId] = useState<number | null>(null)
  const [quantityWarning, setQuantityWarning] = useState(false)
  const { isAuthenticated } = useSession()
  const dispatch = useAppDispatch()
  const t = useTranslations('common')
  const tCart = useTranslations('cart')

  const handleQuantityInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuantity(value)
  }

  const handleBlurInput = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.trim()
    const value = Number(rawValue)

    const step = multiples || 1
    const minQty = minQuantity || 1

    if (rawValue === '' || Number.isNaN(value) || value <= 0) {
      setQuantity(minQty.toString())
      return
    }

    let validValue: number

    if (step === 1) {
      validValue = Math.max(value, minQty)
    } else {
      if (value <= step) {
        validValue = minQty
      } else {
        validValue = valueByMultiples(value, step)
      }
    }

    validValue = valueByMultiples(validValue, step)

    const hasChanged = validValue !== value
    setQuantity(validValue.toString())
    setQuantityWarning(hasChanged)

    if (hasChanged) {
      setQuantityWarning(true)

      setTimeout(() => {
        setQuantityWarning(false)
      }, 3000)
    }
  }

  const handleQuantityButton = (type: 'increase' | 'decrease') => {
    let newQuantity = +quantity

    if (type === 'increase') {
      newQuantity += multiples
    } else {
      newQuantity = Math.max(newQuantity - multiples, minQuantity)
    }

    setQuantity(newQuantity.toString())
  }

  const handleAddToCart = async () => {
    if (+quantity < minQuantity) {
      setQuantity(minQuantity.toString())
      toast.error(`${tCart('mult_message')} (min = ${minQuantity})`)
      return
    }

    const actualPrice = getPrice(+quantity, prices)

    const data: CartItem = {
      id: productId,
      image_id: product.image_id,
      inventory: product.inventory,
      isCheck: false,
      multiples: product.multiples,
      name: product.name,
      price: actualPrice ? actualPrice.price : product.price,
      prices: prices,
      quantity: valueByMultiples(+quantity, product.multiples),
      slug: product.slug,
      supplier: product.supplier,
      totalPrice: +product.price.toFixed(3) * +quantity,
      unit: product.unit ?? t('unit'),
      category_extra_id: categoryExtraId,
      minimum_order_quantity: minQuantity,
      category_extras: product.category_extras ? product.category_extras.sort((a, b) => (a.price ?? 0) - (b.price ?? 0)) : [],
      show_category_extra: product.show_category_extra,
      product_tag_id: product.product_tag?.id ?? 0,
    }

    dispatch(addItem({ cartItem: data, isNew: true }))
    toast.success(t('add_success'))

    if (isAuthenticated) {
      try {
        setLoading(true)
        await cartApiRequest.add({ product_id: productId, quantity: +quantity })
      } catch (error: any) {
        handleApiError({ error, content: 'Có lỗi' })
      } finally {
        setLoading(false)
        setQuantity(minQuantity.toString())
      }
    }
  }

  useEffect(() => {
    if (product.category_extras?.length > 0 && product.show_category_extra) {
      const categoryExtras = product.category_extras.sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
      setCategoryExtraId(categoryExtras[0]?.id ?? null)
    }
  }, [product])

  return (
    <div className={cn(className, 'relative')}>
      {quantityWarning && (
        <div className="absolute bg-black/70 bottom-[calc(100%+4px)] w-full text-white text-center leading-4 text-[13px] px-2 py-0.5 rounded-[2px]">
          {t('quantity_warning', { quantity })}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-black/70"></div>
        </div>
      )}
      <div className="border-separate flex justify-center relative">
        <div className="relative align-middle whitespace-nowrap w-[30%]">
          <button
            onClick={() => handleQuantityButton('decrease')}
            className={clsx('block w-full bg-[#f1f5f9] border border-[#dfdfdf] rounded-tl rounded-bl !text-[#333] px-[10px] h-7 hover:bg-[#e2e8f0] text-lg font-light', {
              '!opacity-50': isDisabled,
            })}
            disabled={isDisabled}
          >
            -
          </button>
        </div>
        <input
          type="tel"
          value={quantity}
          onChange={handleQuantityInput}
          onBlur={handleBlurInput}
          step={1}
          aria-label="Quantity"
          className="border-0 border-y border-y-[#dfdfdf] text-[#333] text-[17px] h-7 p-0 text-center w-[40%] outline-none"
          disabled={isDisabled}
        />
        <div className="relative align-middle whitespace-nowrap w-[30%]">
          <button
            onClick={() => handleQuantityButton('increase')}
            disabled={isDisabled}
            className={clsx('block w-full bg-[#f1f5f9] border border-[#dfdfdf] rounded-tr rounded-br !text-[#333] px-[10px] h-7 hover:bg-[#e2e8f0] text-lg font-light', {
              '!opacity-50': isDisabled,
            })}
          >
            +
          </button>
        </div>
      </div>
      <div className="flex justify-between mt-1 text-gray-800 text-[12.5px]">
        <div>
          {t('min')}: {minQuantity}
        </div>
        <div>
          {t('multiple')}: {multiples}
        </div>
      </div>
      <div className="mt-2 pt-1">
        <Button
          thegioiicPrimary
          full
          size={buttonSize}
          className={clsx('text-[13px]', { '!opacity-50 cursor-not-allowed': isDisabled })}
          onClick={handleAddToCart}
          disabled={loading || isDisabled}
        >
          <Icon icon="tgic icon-shopping-cart-solid" className="mr-2 text-[13px]" />
          {t('add_to_cart')}
        </Button>
      </div>
    </div>
  )
}
